import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { pickCategoryIcon } from "../../lib/categoryIcons";

const VERDICT_BADGE = {
  buy:            { label: "BUY",        cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  book:           { label: "BUY",        cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  consider:       { label: "CONSIDER",   cls: "bg-amber-400/10 text-amber-300 border-amber-400/40" },
  better_value:   { label: "BETTER VALUE", cls: "bg-amber-400/10 text-amber-300 border-amber-400/40" },
  pass:           { label: "PASS",       cls: "bg-red-500/10 text-red-300 border-red-500/40" },
  not_recommended:{ label: "PASS",       cls: "bg-red-500/10 text-red-300 border-red-500/40" },
};

// One product/recommendation tile used across "Today's Picks", "Category
// Winners" and "Hidden Gems". Clicking a *shared* tile goes to the public
// /r/{slug} page; if the recommendation hasn't been shared yet (private)
// we re-run the search via /chat so we still surface the value.
export const PickCard = ({ pick, kicker }) => {
  const verdict = VERDICT_BADGE[pick.verdict_type] || VERDICT_BADGE.consider;
  const CategoryIcon = pickCategoryIcon(pick.category);
  const linkTo = pick.shared && pick.slug
    ? `/r/${pick.slug}`
    : "/chat";
  const linkState = pick.shared && pick.slug
    ? undefined
    : { state: { initial: pick.query_summary } };

  return (
    <Link
      to={linkTo}
      state={linkState?.state}
      data-testid="home-pick-card"
      className="group relative flex flex-col bg-card border border-border rounded-2xl overflow-hidden hover:border-emerald-500/40 transition-all hover-lift"
    >
      <div className="relative h-32 sm:h-36 bg-gradient-to-br from-secondary via-card to-secondary border-b border-border flex items-center justify-center overflow-hidden">
        {pick.image_url ? (
          <img
            src={pick.image_url}
            alt=""
            loading="lazy"
            className={
              pick.image_source === "brand-logo"
                ? "w-16 h-16 object-contain opacity-90"
                : "w-full h-full object-contain p-3"
            }
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <CategoryIcon className="w-9 h-9 text-emerald-500/60" strokeWidth={1.5} />
        )}
        <span
          className={`absolute top-2.5 left-2.5 text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-md border ${verdict.cls}`}
        >
          {verdict.label}
        </span>
        {pick.value_score != null && (
          <span className="absolute top-2.5 right-2.5 text-[10px] font-bold px-2 py-0.5 rounded-md bg-background/80 border border-border text-emerald-400">
            {pick.value_score}/10
          </span>
        )}
      </div>
      <div className="flex flex-col p-3.5 flex-1">
        {kicker && (
          <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold mb-1">
            {kicker}
          </div>
        )}
        <div className="font-display font-semibold text-sm text-foreground leading-tight mb-1 line-clamp-2">
          {pick.best_product || pick.query_summary}
        </div>
        {pick.tagline && (
          <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2 mb-3">
            {pick.tagline}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-semibold">
            {pick.category_label || pick.category || "—"}
          </span>
          <span className="flex items-center gap-0.5 text-[11px] text-emerald-500 font-medium opacity-0 group-hover:opacity-100 transition">
            See verdict <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
      {pick.confidence != null && (
        <div
          className="absolute inset-x-0 bottom-0 h-0.5 bg-emerald-500/40"
          style={{ width: `${Math.min(100, Math.max(0, pick.confidence))}%` }}
          aria-hidden
        />
      )}
      {pick.shared && (
        <span
          className="absolute bottom-2 right-2 text-emerald-500/80"
          aria-label="Verified by community"
          title="Verified by community"
        >
          <ShieldCheck className="w-3 h-3" />
        </span>
      )}
    </Link>
  );
};

export default PickCard;
